import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, message, Descriptions, DatePicker, Select, Row, Col, Statistic } from 'antd';
import { CheckOutlined, EyeOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * 中文函数注释：根据后端记录生成状态标签
 */
function getStatusTag(status) {
  const map = {
    pending: { color: 'orange', text: '待审批' },
    approved: { color: 'green', text: '已通过' },
    rejected: { color: 'red', text: '已拒绝' }
  };
  const cfg = map[status] || { color: 'default', text: status };
  return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

/**
 * 中文函数注释：将后端返回的调休记录映射为表格行数据
 */
function mapRecord(r) {
  return {
    id: r.id,
    compDate: r.comp_date,
    duration: r.duration,
    reason: r.reason,
    status: r.status,
    createdAt: r.created_at || r.createdAt,
    approvedAt: r.approved_at || r.approvedAt,
    applicantName: r.applicant?.real_name || r.applicant?.realName || r.applicant?.username,
    approverName: r.approver?.real_name || r.approver?.realName || r.approver?.username,
    projectName: r.project?.name,
    taskTitle: r.task?.title
  };
}

/**
 * 中文函数注释：调休审批页面，管理员/主管用于查看与审批调休记录
 * 功能：
 * 1）按状态与日期范围筛选调休记录
 * 2）查看详情
 * 3）审批（通过/拒绝）
 */
export default function CompensatoryManagement() {
  const { token, isAuthenticated } = useAuthStore();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: 'pending', dateRange: null });
  const [form] = Form.useForm();

  /**
   * 中文函数注释：获取调休记录列表
   */
  const fetchRecords = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
        params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
      }
      const url = `${apiUrl}/compensatory${params.toString() ? `?${params.toString()}` : ''}`;
      const resp = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!resp.ok) throw new Error('加载失败');
      const data = await resp.json();
      const rows = data?.data?.records || data?.records || [];
      setRecords(rows.map(mapRecord));
    } catch (e) {
      console.error(e);
      message.error('加载调休记录失败');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (isAuthenticated) fetchRecords();
  }, [isAuthenticated, fetchRecords]);

  /**
   * 中文函数注释：提交审批（仅发送状态，后端当前不存储审批意见）
   */
  const handleApproval = async (values) => {
    if (!token || !selected?.id) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const resp = await fetch(`${apiUrl}/compensatory/${selected.id}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: values.status })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || '审批失败');
      }
      message.success('审批完成');
      setApprovalVisible(false);
      setDetailVisible(false);
      form.resetFields();
      fetchRecords();
    } catch (e) {
      console.error(e);
      message.error(e.message || '审批失败');
    }
  };

  /**
   * 中文函数注释：根据筛选条件输出表格数据
   */
  const dataSource = useMemo(() => {
    return records;
  }, [records]);

  // 表格列
  const columns = [
    {
      title: '申请人',
      dataIndex: 'applicantName',
      key: 'applicantName',
      render: (t) => (<Space><UserOutlined />{t}</Space>)
    },
    {
      title: '调休日期',
      dataIndex: 'compDate',
      key: 'compDate',
      render: (d) => (<Space><CalendarOutlined />{dayjs(d).format('YYYY-MM-DD')}</Space>)
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      render: (v) => `${v}小时`
    },
    {
      title: '项目',
      dataIndex: 'projectName',
      key: 'projectName'
    },
    {
      title: '任务',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => d ? dayjs(d).format('MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => { setSelected(record); setDetailVisible(true); }}>详情</Button>
          {record.status === 'pending' && (
            <Button type="link" icon={<CheckOutlined />} onClick={() => { setSelected(record); setApprovalVisible(true); }}>审批</Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* 统计看板 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="待审批" value={records.filter(r => r.status === 'pending').length} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已通过" value={records.filter(r => r.status === 'approved').length} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="已拒绝" value={records.filter(r => r.status === 'rejected').length} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="总调休时长" value={records.reduce((s, r) => s + Number(r.duration || 0), 0)} suffix="小时" valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
      </Row>

      {/* 过滤器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <span>状态：</span>
            <Select value={filters.status} style={{ width: 120 }} onChange={(v) => setFilters({ ...filters, status: v })}>
              <Option value="all">全部</Option>
              <Option value="pending">待审批</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Col>
          <Col>
            <span>日期范围：</span>
            <RangePicker value={filters.dateRange} onChange={(v) => setFilters({ ...filters, dateRange: v })} />
          </Col>
          <Col>
            <Button onClick={() => setFilters({ status: 'pending', dateRange: null })}>重置</Button>
          </Col>
          <Col>
            <Button type="primary" onClick={fetchRecords}>查询</Button>
          </Col>
        </Row>
      </Card>

      {/* 列表 */}
      <Card title="调休申请列表">
        <Table columns={columns} dataSource={dataSource} loading={loading} rowKey="id" pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }} />
      </Card>

      {/* 详情对话框 */}
      <Modal title="调休申请详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={[<Button key="close" onClick={() => setDetailVisible(false)}>关闭</Button>, selected?.status === 'pending' && (<Button key="approve" type="primary" onClick={() => { setDetailVisible(false); setApprovalVisible(true); }}>审批</Button>)]} width={600}>
        {selected && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="申请人" span={2}>{selected.applicantName}</Descriptions.Item>
            <Descriptions.Item label="调休日期">{dayjs(selected.compDate).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label="时长">{selected.duration}小时</Descriptions.Item>
            <Descriptions.Item label="项目">{selected.projectName || '-'}</Descriptions.Item>
            <Descriptions.Item label="任务" span={2}>{selected.taskTitle || '-'}</Descriptions.Item>
            <Descriptions.Item label="原因" span={2}>{selected.reason || '-'}</Descriptions.Item>
            <Descriptions.Item label="申请状态">{getStatusTag(selected.status)}</Descriptions.Item>
            <Descriptions.Item label="申请时间">{selected.createdAt ? dayjs(selected.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            {selected.approverName && (
              <>
                <Descriptions.Item label="审批人">{selected.approverName}</Descriptions.Item>
                <Descriptions.Item label="审批时间">{selected.approvedAt ? dayjs(selected.approvedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 审批对话框 */}
      <Modal title="审批调休申请" open={approvalVisible} onCancel={() => { setApprovalVisible(false); form.resetFields(); }} footer={null} width={500}>
        {selected && (
          <>
            <Descriptions column={1} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="申请人">{selected.applicantName}</Descriptions.Item>
              <Descriptions.Item label="调休时间">{dayjs(selected.compDate).format('YYYY-MM-DD')}</Descriptions.Item>
              <Descriptions.Item label="时长">{selected.duration}小时</Descriptions.Item>
              <Descriptions.Item label="原因">{selected.reason || '-'}</Descriptions.Item>
            </Descriptions>
            <Form form={form} layout="vertical" onFinish={handleApproval}>
              <Form.Item name="status" label="审批结果" rules={[{ required: true, message: '请选择审批结果' }]}>
                <Select placeholder="请选择审批结果">
                  <Option value="approved">通过</Option>
                  <Option value="rejected">拒绝</Option>
                </Select>
              </Form.Item>
              <Form.Item name="approvalComment" label="审批意见">
                <TextArea rows={4} placeholder="可填写审批说明，仅内部参考（当前后端未保存）" maxLength={500} showCount />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { setApprovalVisible(false); form.resetFields(); }}>取消</Button>
                  <Button type="primary" htmlType="submit">提交审批</Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
}